import apiClient from "../request/http-common";

class P2PMarketplaceService {
    
    // Get marketplace offers
    async getOffers(filters = {}) {
        try {
            const params = new URLSearchParams({
                type: filters.tradeType || 'buy',  // API expects 'type' not 'trade_type'
                cryptocurrency: filters.cryptocurrency || 'USDT',
                fiat_currency: filters.fiatCurrency || 'USD',
                ...(filters.paymentMethod && { payment_method: filters.paymentMethod }),
                ...(filters.amount && { amount: filters.amount }),
                ...(filters.minAmount && { min_amount: filters.minAmount }),
                ...(filters.maxAmount && { max_amount: filters.maxAmount })
            });

            const response = await apiClient.get(`/api/p2p/offers?${params}`);
            return response.data;
        } catch (error) {
            console.error('Get offers error:', error);
            throw error;
        }
    }

    // Create a new P2P order
    async createOrder(orderData) {
        try {
            const response = await apiClient.post('/api/p2p/orders', orderData);
            return response.data;
        } catch (error) {
            console.error('Create order error:', error);
            throw error;
        }
    }

    // Get user's orders
    async getMyOrders(status = '', page = 1) {
        try {
            const params = new URLSearchParams({
                page,
                per_page: 20,
                ...(status && { status })
            });

            const response = await apiClient.get(`/api/p2p/my-orders?${params}`);
            return response.data;
        } catch (error) {
            console.error('Get my orders error:', error);
            throw error;
        }
    }

    // Get order details
    async getOrderDetails(orderId) {
        try {
            const response = await apiClient.get(`/api/p2p/orders/${orderId}`);
            return response.data;
        } catch (error) {
            console.error('Get order details error:', error);
            throw error;
        }
    }

    // Upload payment proof
    async uploadPaymentProof(orderId, file, notes = '') {
        try {
            const formData = new FormData();
            formData.append('proof_file', file);
            formData.append('notes', notes);

            const response = await apiClient.post(`/api/p2p/orders/${orderId}/payment-proof`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data;
        } catch (error) {
            console.error('Upload payment proof error:', error);
            throw error;
        }
    }

    // Confirm payment received (for seller)
    async confirmPayment(orderId) {
        try {
            const response = await apiClient.post(`/api/p2p/orders/${orderId}/confirm-payment`);
            return response.data;
        } catch (error) {
            console.error('Confirm payment error:', error);
            throw error;
        }
    }

    // Cancel order
    async cancelOrder(orderId, reason = '') {
        try {
            const response = await apiClient.post(`/api/p2p/orders/${orderId}/cancel`, {
                reason
            });
            return response.data;
        } catch (error) {
            console.error('Cancel order error:', error);
            throw error;
        }
    }

    // Create dispute
    async createDispute(orderId, reason, description = '') {
        try {
            const response = await apiClient.post(`/api/p2p/orders/${orderId}/dispute`, {
                reason,
                description
            });
            return response.data;
        } catch (error) {
            console.error('Create dispute error:', error);
            throw error;
        }
    }

    // Get order history with pagination (using my-orders with completed status)
    async getOrderHistory(page = 1, limit = 20) {
        try {
            const params = new URLSearchParams({
                page,
                per_page: limit,
                status: 'completed' // Get completed orders as history
            });

            const response = await apiClient.get(`/api/p2p/my-orders?${params}`);
            return response.data;
        } catch (error) {
            console.error('Get order history error:', error);
            throw error;
        }
    }

    // Get P2P statistics
    async getStatistics() {
        try {
            const response = await apiClient.get('/api/p2p/stats'); // Correct endpoint
            return response.data;
        } catch (error) {
            console.error('Get statistics error:', error);
            throw error;
        }
    }

    // Get available payment methods
    async getPaymentMethods() {
        try {
            const response = await apiClient.get('/api/p2p/payment-methods');
            return response.data;
        } catch (error) {
            console.error('Get payment methods error:', error);
            throw error;
        }
    }

    // Rate a partner after successful trade
    async ratePartner(orderId, rating, comment = '') {
        try {
            const response = await apiClient.post(`/api/p2p/orders/${orderId}/rate`, {
                rating,
                comment
            });
            return response.data;
        } catch (error) {
            console.error('Rate partner error:', error);
            throw error;
        }
    }

    // Report a partner
    async reportPartner(partnerId, reason, description = '') {
        try {
            const response = await apiClient.post(`/api/p2p/report-partner`, {
                partner_id: partnerId,
                reason,
                description
            });
            return response.data;
        } catch (error) {
            console.error('Report partner error:', error);
            throw error;
        }
    }

    // Get partner details
    async getPartnerDetails(partnerId) {
        try {
            const response = await apiClient.get(`/api/p2p/partners/${partnerId}`);
            return response.data;
        } catch (error) {
            console.error('Get partner details error:', error);
            throw error;
        }
    }

    // Search partners
    async searchPartners(filters = {}) {
        try {
            const params = new URLSearchParams({
                ...(filters.country && { country: filters.country }),
                ...(filters.rating && { min_rating: filters.rating }),
                ...(filters.verified && { verified: filters.verified }),
                ...(filters.cryptocurrency && { cryptocurrency: filters.cryptocurrency }),
                ...(filters.fiatCurrency && { fiat_currency: filters.fiatCurrency })
            });

            const response = await apiClient.get(`/api/p2p-partners?${params}`);
            return response.data;
        } catch (error) {
            console.error('Search partners error:', error);
            throw error;
        }
    }

    // Release coins for a paid order
    async releaseCoins(orderId) {
        try {
            const response = await apiClient.post(`/api/p2p/orders/${orderId}/release-coins`);
            return response.data;
        } catch (error) {
            console.error('Release coins error:', error);
            throw error;
        }
    }

    // Get chat messages for an order
    async getChatMessages(orderId) {
        try {
            const response = await apiClient.get(`/api/p2p/orders/${orderId}/chat`);
            return response.data;
        } catch (error) {
            console.error('Get chat messages error:', error);
            throw error;
        }
    }

    // Send chat message for an order
    async sendChatMessage(orderId, message) {
        try {
            const response = await apiClient.post(`/api/p2p/orders/${orderId}/chat`, {
                message
            });
            return response.data;
        } catch (error) {
            console.error('Send chat message error:', error);
            throw error;
        }
    }
}

export default new P2PMarketplaceService();
